<template>
  <Teleport to="body">
    <div :id="id" ref="element" class="modal app-modal" :class="modalClass" tabindex="-1" v-bind="$attrs" :aria-label="`${id} popup`">
      <div class="modal-dialog" :class="computedDialogClasses">
        <div v-if="modalShown" class="modal-content" :class="contentClass">
          <div v-if="!hideHeader" class="modal-header" :class="headerClass">
            <slot name="header">
              <component :is="titleTag" v-if="title" class="modal-title" :class="titleClass">
                <slot name="title">
                  {{ title }}
                </slot>
              </component>
              <div class="icon-buttons">
                <i class="icon icon-heart-filled-long size-sm" />
                <button v-if="!hideHeaderClose" type="button" class="btn-close" data-bs-dismiss="modal">
                  <i class="icon icon-cross size-sm" />
                </button>
              </div>
            </slot>
          </div>
          <div class="modal-body" :class="bodyClass">
            <slot />
          </div>
          <div v-if="!hideFooter" class="modal-footer" :class="footerClass">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useModalStore } from "@/store/modal";
import BModal from "bootstrap/js/src/modal";
import { amplitudeTrack } from "@/utils/amplitude";
import { sendHjEvent } from "@/utils/hotjar";
import { ModalType } from "@/interfaces/ModalType.ts";

defineOptions({
  name: "app-modal",
  inheritAttrs: true,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
  centered: {
    type: Boolean,
    default: true,
  },
  scrollable: {
    type: Boolean,
    default: false,
  },
  headerClass: {
    type: String,
    default: "",
  },
  bodyClass: {
    type: String,
    default: "",
  },
  contentClass: {
    type: String,
    default: "",
  },
  dialogClass: {
    type: String,
    default: "",
  },
  footerClass: {
    type: String,
    default: "",
  },
  modalClass: {
    type: String,
    default: "",
  },
  hideBackdrop: {
    type: Boolean,
    default: false,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  hideFooter: {
    type: Boolean,
    default: false,
  },
  hideHeaderClose: {
    type: Boolean,
    default: false,
  },
  noCloseOnBackdrop: {
    type: Boolean,
    default: false,
  },
  noCloseOnEsc: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  titleClass: {
    type: String,
    default: "",
  },
  titleTag: {
    type: String,
    default: "h4",
  },
});

const emit = defineEmits(["update:modelValue", "show", "hide", "shown", "hidden", "hide-prevented"]);
const modalStore = useModalStore();

const element = ref<HTMLElement | null>(null);
const instance = ref<any>(null);
const modalShown = ref(false);

const modal = computed(() => modalStore.getShownModalById(props.id));

const computedDialogClasses = computed(() => {
  return [
    {
      ["modal-dialog-centered"]: props.centered,
      ["modal-dialog-scrollable"]: props.scrollable,
    },
    props.dialogClass,
  ];
});

// Methods
function onShow(e: any) {
  emit("show", e);
  modalShown.value = !e.defaultPrevented;
}

function onHide(e: any) {
  emit("hide", e);

  if (!e.defaultPrevented) {
    modalShown.value = false;
  }
}

function onShown() {
  emit("shown");
  const popupName = props.id.split("-").pop();
  const eventName = `Popup ${popupName} shown`;

  amplitudeTrack(eventName);
  sendHjEvent(eventName);
}

function onHidden() {
  emit("hidden");
}

function onHidePrevented() {
  emit("hide-prevented");
}

function showModalAction(modalData: ModalType) {
  modalStore.showModal(modalData);
}

function hideModalAction(modalId: string) {
  modalStore.hideModal(modalId);
}

watch(
  () => props.modelValue,
  (newValue) => {
    modalShown.value = !!newValue;
  },
);

watch(modalShown, (newValue) => {
  nextTick(() => {
    if (newValue) {
      instance.value?.show();
      showModalAction({ id: props.id });
    } else {
      instance.value?.hide();
      hideModalAction(props.id);
    }

    emit("update:modelValue", newValue);
  });
});

watch(modal, (newValue) => {
  modalShown.value = !!newValue;
});

onMounted(() => {
  nextTick(() => {
    if (!element.value) {
      return;
    }

    instance.value = new BModal(element.value, {
      backdrop: props.hideBackdrop ? false : props.noCloseOnBackdrop ? "static" : true,
      keyboard: !props.noCloseOnEsc,
    });

    element.value.addEventListener("show.bs.modal", onShow);
    element.value.addEventListener("shown.bs.modal", onShown);
    element.value.addEventListener("hide.bs.modal", onHide);
    element.value.addEventListener("hidden.bs.modal", onHidden);
    element.value.addEventListener("hidePrevented.bs.modal", onHidePrevented);

    if (props.modelValue || modal.value) {
      instance.value.show();
    }
  });
});

onBeforeUnmount(() => {
  if (!element.value) {
    return;
  }

  element.value.removeEventListener("show.bs.modal", onShow);
  element.value.removeEventListener("shown.bs.modal", onShown);
  element.value.removeEventListener("hide.bs.modal", onHide);
  element.value.removeEventListener("hidden.bs.modal", onHidden);
  element.value.removeEventListener("hidePrevented.bs.modal", onHidePrevented);
});
</script>

<style lang="scss">
@import "./index.scss";
</style>
